import React, { Children, forwardRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography } from '../typography/Typography';
import ProgressStepperContext from './ProgressStepperContext';

const calculateGridTemplateColumns = (centerEdgeNodes, numberOfNodes) => {
  if (centerEdgeNodes || numberOfNodes < 3) {
    return {
      gridTemplateColumns: `repeat(${numberOfNodes}, 1fr)`
    };
  }
  const edgeSize = 100 / (numberOfNodes - 1) / 2;
  const rounded = Math.round(edgeSize * 100) / 100;
  return {
    gridTemplateColumns: `${rounded}% repeat(${numberOfNodes - 2}, 1fr) ${rounded}%`
  };
};

/**
 * A `ProgressStepper` displays information about the completion status of a task or process.
 *
 * The `useProgressStepper` hook gives access the following context values on `ProgressStepper` sub-components:
 * - `isVertical`
 * - `activeStep`
 * - `nonLinear`
 * - `centerEdgeNodes`
 * - `connector`
 *
 * Use `ProgressStepper` with the following sub-components: [Step](#step), [StepLabel](#steplabel),
 * [StepButton](#stepbutton), [StepContent](#stepcontent)
 *
 * Usage:
 *
 * ```jsx
 * import { ProgressStepper } from '@one-thd/sui-atomic-components';
 * ```
 */
const ProgressStepper = forwardRef((props, ref) => {

  const {
    orientation = 'horizontal',
    children,
    centerEdgeNodes = false,
    nonLinear = false,
    activeStep: activeStepProp = -1,
    connector,
    leadingLabel: leadingLabelProp,
    trailingLabel: trailingLabelProp,
    ...rest
  } = props;

  const isVertical = orientation === 'vertical';
  const classes = classNames('sui-list-none sui-min-w-min', {
    'sui-grid': !isVertical,
    'sui-inline-flex sui-flex-col': isVertical
  });

  const childrenArray = Children.toArray(children).filter(Boolean);
  const templateColumns = calculateGridTemplateColumns(centerEdgeNodes, childrenArray.length);
  const olStyles = isVertical ? {} : templateColumns;

  const steps = childrenArray.map((step, index) => {
    let align = 'center';
    if ((!centerEdgeNodes && index === 0) || isVertical) {
      align = 'start';
    } else if (!centerEdgeNodes && index === childrenArray.length - 1) {
      align = 'end';
    }
    const { 'aria-label': ariaLabelProp, ...stepRest } = step.props;
    const ariaLabel = ariaLabelProp || `Step ${(index + 1)} out of ${childrenArray.length}.`;
    return React.cloneElement(step, {
      align,
      last: index === childrenArray.length - 1,
      index,
      'aria-label': ariaLabel,
      ...stepRest
    });
  });

  const leadingLabel = typeof leadingLabelProp === 'string'
    ? <Typography color="subtle">{leadingLabelProp}</Typography>
    : leadingLabelProp;
  const trailingLabel = typeof trailingLabelProp === 'string'
    ? <Typography color="subtle">{trailingLabelProp}</Typography>
    : trailingLabelProp;

  const activeStep = Number(activeStepProp);
  const contextValue = useMemo(
    () => ({
      isVertical, activeStep, nonLinear, centerEdgeNodes, connector
    }),
    [isVertical, activeStep, nonLinear, centerEdgeNodes, connector]
  );

  const hasLabels = leadingLabel || trailingLabel;

  return (
    <ProgressStepperContext.Provider value={contextValue}>
      <div
        ref={ref}
        {...rest}
      >
        {hasLabels && (
          <div className="sui-flex sui-justify-between sui-mt-2">
            <div className="sui-justify-self-start">{leadingLabel}</div>
            <div className="sui-justify-self-end">{trailingLabel}</div>
          </div>
        )}
        <ol className={classes} style={olStyles}>
          {steps}
        </ol>
      </div>
    </ProgressStepperContext.Provider>
  );
});

ProgressStepper.displayName = 'ProgressStepper';

ProgressStepper.propTypes = {
  /**
   * The label to be placed on the top left corner.
   */
  leadingLabel: PropTypes.node,
  /**
   * The label to be placed on the top right corner.
   */
  trailingLabel: PropTypes.node,
  /**
   * The component orientation.
   */
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  /**
   * If set, will center the first and last step nodes. Defaults to false
   * @default false
   */
  centerEdgeNodes: PropTypes.bool,
  /**
   * Two or more `Step` components.
   */
  children: PropTypes.node,
  /**
   * Set the active step (zero-based index). Set to -1 by default to deactivate all steps.
   * @default -1
   */
  activeStep: PropTypes.number,
  /**
   * If set, the component wiill not assist in controlling steps in a linear flow.
   * @default false
   */
  nonLinear: PropTypes.bool,
  /**
   * An element to be placed between each step.
   */
  connector: PropTypes.node
};

export { ProgressStepper };